import React, { useState, createContext, useEffect, useContext } from 'react'
import preferencesService from '../../services/PreferencesService'
import SoundService from '../../services/SoundService'
import { UserContext } from './UserContext'
import i18n from '../../i18n/config'

export const SettingsContext = createContext(null)

// Il tema applicato e isDarkMode vanno calcolati dallo STESSO valore: con
// preferenze senza `theme` (account on-prem creati da seed-admin con {}) lo
// sfondo cadeva su darker-monochrome ma isDarkMode restava false, e le icone
// uscivano blu notte su nero, come disattivate (collaudo 07/10/2026).
const DEFAULT_THEME = 'darker-monochrome'
const DARK_THEMES = ['dark', 'dark-monochrome', 'darker-monochrome']
const resolveTheme = (prefs) => (prefs && prefs.theme) || DEFAULT_THEME
const isDarkTheme = (theme) => DARK_THEMES.includes(theme)

const SettingsContextProvider = ({ children }) => {
  const { isAuthenticated, user } = useContext(UserContext)

  const [currentTheme, setCurrentTheme] = useState(() => {
    const savedTheme = localStorage.getItem('currentTheme')
    return savedTheme || 'dark'
  })

  // Legacy support for isDarkMode
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('isDarkMode')
    return savedMode !== null ? JSON.parse(savedMode) : true
  })

  // Sound notifications enabled state
  const [isSoundEnabled, setIsSoundEnabled] = useState(() => {
    const savedSound = localStorage.getItem('isSoundEnabled')
    // Default to true if not set
    return savedSound !== null ? JSON.parse(savedSound) : true
  })

  const [preferences, setPreferences] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  // Initialize preferences service
  useEffect(() => {
    const initPreferences = async () => {
      try {
        await preferencesService.init()
        preferencesService.syncWithExistingSettings()

        // Set initial state from preferences
        const prefs = preferencesService.getPreferences()
        if (prefs) {
          setPreferences(prefs)
          const theme = resolveTheme(prefs)
          setCurrentTheme(theme)
          setIsDarkMode(isDarkTheme(theme))

          // Load sound preference
          if (prefs.sound_enabled !== undefined) {
            setIsSoundEnabled(prefs.sound_enabled)
            SoundService.setEnabled(prefs.sound_enabled)
          }
        }

        // Add listener for preference changes
        preferencesService.addListener((newPreferences) => {
          setPreferences(newPreferences)
          if (newPreferences) {
            const theme = resolveTheme(newPreferences)
            setCurrentTheme(theme)
            setIsDarkMode(isDarkTheme(theme))

            if (newPreferences.sound_enabled !== undefined) {
              setIsSoundEnabled(newPreferences.sound_enabled)
              SoundService.setEnabled(newPreferences.sound_enabled)
            }
          }
        })

        preferencesService.setupThemeListener()
      } catch (error) {
        console.error('Failed to initialize preferences:', error)
      } finally {
        setIsLoading(false)
      }
    }

    initPreferences()
  }, [])

  // Sync i18n language with preferences
  useEffect(() => {
    if (preferences?.language) {
      if (i18n.language !== preferences.language) {
        i18n.changeLanguage(preferences.language).catch((error) => {
          console.error('Failed to change language:', error)
        })
      }
    }
  }, [preferences?.language])

  const switchMode = async () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)

    // Update localStorage for backward compatibility
    localStorage.setItem('isDarkMode', JSON.stringify(newMode))

    // Update preferences in backend
    try {
      await preferencesService.updateTheme(newMode ? 'dark' : 'light')
    } catch (error) {
      console.error('Failed to update theme preference:', error)
    }
  }

  const switchTheme = async (theme) => {
    // Update local state immediately for responsive UI
    setCurrentTheme(theme)
    setIsDarkMode(
      theme === 'dark' ||
        theme === 'dark-monochrome' ||
        theme === 'darker-monochrome'
    )

    // Update localStorage
    localStorage.setItem('currentTheme', theme)
    localStorage.setItem(
      'isDarkMode',
      JSON.stringify(
        theme === 'dark' ||
          theme === 'dark-monochrome' ||
          theme === 'darker-monochrome'
      )
    )

    // Update preferences in backend
    try {
      await preferencesService.updateTheme(theme)
    } catch (error) {
      console.error('Failed to update theme preference:', error)
      // Don't revert the local state - keep the theme change even if backend fails
      // The user will still see the theme change, and it will be saved locally
      // The PreferencesService already applies the theme locally as a fallback

      // Check if it's an authentication error
      if (error.message && error.message.includes('401')) {
        console.warn(
          'Theme applied locally due to authentication failure - please log in again to sync with server'
        )
      }
    }
  }

  const switchSoundEnabled = async () => {
    const newValue = !isSoundEnabled
    setIsSoundEnabled(newValue)

    // Update SoundService
    SoundService.setEnabled(newValue)

    // Update localStorage
    localStorage.setItem('isSoundEnabled', JSON.stringify(newValue))

    // Update preferences in backend
    try {
      await preferencesService.updatePreference('sound_enabled', newValue)
    } catch (error) {
      console.error('Failed to update sound preference:', error)
    }
  }

  // Legacy useEffect for backward compatibility
  useEffect(() => {
    const savedTheme = JSON.parse(localStorage.getItem('isDarkMode'))
    if (savedTheme !== null) {
      setIsDarkMode(savedTheme)
    }
  }, [setIsDarkMode])

  // Initialize sound service with saved preference
  useEffect(() => {
    const savedSound = localStorage.getItem('isSoundEnabled')
    if (savedSound !== null) {
      const enabled = JSON.parse(savedSound)
      SoundService.setEnabled(enabled)
    }
  }, [])

  // Reload preferences when user logs in
  useEffect(() => {
    const reloadPreferencesOnLogin = async () => {
      if (isAuthenticated && user) {
        try {
          // Reload preferences from backend
          const prefs = await preferencesService.loadPreferences()
          if (prefs) {
            setPreferences(prefs)
            const theme = resolveTheme(prefs)
            setCurrentTheme(theme)
            setIsDarkMode(isDarkTheme(theme))
            // Apply theme immediately
            preferencesService.applyTheme()

            // Load sound preference
            if (prefs.sound_enabled !== undefined) {
              setIsSoundEnabled(prefs.sound_enabled)
              SoundService.setEnabled(prefs.sound_enabled)
            }
          }
        } catch (error) {
          console.error('Failed to reload preferences on login:', error)
        }
      }
    }

    reloadPreferencesOnLogin()
  }, [isAuthenticated, user])

  return (
    <SettingsContext.Provider
      value={{
        isDarkMode,
        currentTheme,
        isSoundEnabled,
        switchMode,
        switchTheme,
        switchSoundEnabled,
        preferences,
        isLoading,
        preferencesService,
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}

export default SettingsContextProvider
